import type { FirstAccountSetupStepId } from "../../lib/firstAccountSetup";
import type { SetupStepId } from "./firstAccountSetupData";
import { useFirstRunEnvironment, type FirstRunEnvironmentPhase } from "./useFirstRunEnvironment";
import type { ForgeWorkspaceMembership } from "../../workspace/resolveCurrentForgeWorkspace";

/** The environment-related props `FirstAccountSetup` needs, shaped from the raw hook state. */
export interface FirstRunEnvironmentStepProps {
  readonly initialStepId: SetupStepId;
  readonly environmentPhase: FirstRunEnvironmentPhase;
  readonly environmentId: string | null;
  readonly environmentError: string | null;
  readonly environmentWorkspaceLabel: string | null;
  /**
   * Populated only when the account belongs to more than one CMMD workspace
   * and has not picked one yet (CMMD-Center/forge#3572): the panel renders a
   * workspace selector instead of the ensure control until one is chosen.
   */
  readonly environmentWorkspaceOptions: ReadonlyArray<ForgeWorkspaceMembership>;
  readonly onSelectEnvironmentWorkspace: (organizationId: number) => void;
  readonly onEnsureEnvironment: () => void;
}

/**
 * Wraps `useFirstRunEnvironment` and folds its result into the exact props
 * `FirstAccountSetup` renders, including the "environment" step override: the
 * catalog has no environment yet, so the wizard opens there regardless of
 * whatever step was last persisted.
 */
export function useFirstRunEnvironmentStep(input: {
  readonly activeStepId: FirstAccountSetupStepId;
}): FirstRunEnvironmentStepProps {
  const {
    catalogEnvironmentId,
    environmentError,
    environmentId,
    environmentPhase,
    pendingWorkspaceLabel,
    workspaceOptions,
    selectWorkspace,
    ensure,
  } = useFirstRunEnvironment();

  return {
    initialStepId: catalogEnvironmentId === null ? "environment" : input.activeStepId,
    environmentPhase,
    environmentId,
    environmentError,
    environmentWorkspaceLabel: pendingWorkspaceLabel,
    environmentWorkspaceOptions: workspaceOptions,
    onSelectEnvironmentWorkspace: selectWorkspace,
    onEnsureEnvironment: ensure,
  };
}
